import React, { useState, ReactNode } from 'react';
import { LinkDoc } from 'components/userPanel/sessions/components/LinkDoc';
import { Link } from 'components/_new/Link';
import { ArticlePresenterDialog } from 'components/knowledgeBase/ArticlePresenterDialog';
import { usePanel } from 'contexts/panelContext';
import { useParams } from 'react-router-dom';
import { RouterUrlParams } from 'App';
import { KnowledgeBaseArticleLink } from '../../../knowledgeBase/KnowledgeBaseLink';

interface LinkDocArticleWithIconWrapperProps {
	articleId?: number;
	articleLabel?: string;
	children?: ReactNode;
	externalUrl?: string;
	showExternalIcon?: boolean;
	contentParserOptions?: any;
	className?: string;
}

export const LinkDocArticleWithIconWrapper: React.FC<LinkDocArticleWithIconWrapperProps> = ({
	articleId,
	articleLabel,
	children,
	externalUrl,
	showExternalIcon = true,
	contentParserOptions,
	className,
}) => {
	const [currentArticleId, setCurrentArticleId] = useState<number | null>(null);
	const { panelLinkPrefix } = usePanel();
	const { organizationName, programName, type } = useParams() as RouterUrlParams;

	const knowledgeBaseLinkPrefix = `${panelLinkPrefix}/knowledge-base`;

	const defaultContentParserOptions = {
		handlers: {
			onArticleLinkClick: (id: number) => {
				setCurrentArticleId(id);
			},
		},
		replaces: {
			panelPrefix: panelLinkPrefix,
			organization: organizationName,
			program: programName,
			usertype: type,
		},
		disableFormat: true,
		...contentParserOptions,
	};

	if (articleLabel) {
		return (
			<>
				<ArticlePresenterDialog
					articleId={currentArticleId}
					visible={Boolean(currentArticleId)}
					onHide={() => setCurrentArticleId(null)}
					contentParserOptions={defaultContentParserOptions}
					knowledgeBaseLinkPrefix={knowledgeBaseLinkPrefix}
				/>
				<KnowledgeBaseArticleLink
					label={articleLabel}
					render={({ to, key, label }) => {
						const extractedArticleId = key.startsWith('kba-')
							? parseInt(key.replace('kba-', ''), 10)
							: null;
						const finalExternalUrl = externalUrl || `/${panelLinkPrefix}/${to.pathname}`;

						if (!extractedArticleId || Number.isNaN(extractedArticleId)) {
							return '[link error]';
						}

						return (
							<span className={className}>
								<LinkDoc
									to={{}}
									onClick={(event: React.MouseEvent) => {
										event.preventDefault();
										setCurrentArticleId(extractedArticleId);
									}}
								>
									{children || label}
								</LinkDoc>

								{showExternalIcon && (
									<Link
										to={{ pathname: finalExternalUrl }}
										target="_blank"
										icon="arrow-up-right-from-square"
										iconStyle="solid"
										iconOnly
									/>
								)}
							</span>
						);
					}}
				>
					{children}
				</KnowledgeBaseArticleLink>
			</>
		);
	}

	if (articleId) {
		const defaultExternalUrl = `/knowledge-base/article/${articleId}`;
		const finalExternalUrl = externalUrl || `/${panelLinkPrefix}${defaultExternalUrl}`;

		const handleArticleLinkClick = (event: React.MouseEvent) => {
			event.preventDefault();
			setCurrentArticleId(articleId);
		};

		return (
			<>
				<ArticlePresenterDialog
					articleId={currentArticleId}
					visible={Boolean(currentArticleId)}
					onHide={() => setCurrentArticleId(null)}
					contentParserOptions={defaultContentParserOptions}
					knowledgeBaseLinkPrefix={knowledgeBaseLinkPrefix}
				/>

				<span className={className}>
					<LinkDoc to={finalExternalUrl} onClick={handleArticleLinkClick}>
						{children}
					</LinkDoc>
					{showExternalIcon && (
						<Link
							to={finalExternalUrl}
							target="_blank"
							icon="arrow-up-right-from-square"
							iconStyle="solid"
							iconOnly
						/>
					)}
				</span>
			</>
		);
	}

	return null;
};
